import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Link } from "react-router-dom";
import { api } from "../api/apiClient";
import type { Location, Shift, UserListEntry, VacationRequest } from "../api/types";
import { BulkAddShiftsModal } from "./BulkAddShiftsModal";
import { DayShiftsModal } from "./DayShiftsModal";
import { MonthCalendar } from "./MonthCalendar";
import { ShiftChip } from "./ShiftChip";
import { useAuth } from "../state/AuthContext";
import { buildScheduleCandidateUsers } from "../utils/scheduleCandidateUsers";

export function startOfScheduleMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

export function endOfScheduleMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}

export function scheduleDayKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function scheduleSameDay(a: Date, b: Date) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export interface ScheduleWorkspaceProps {
  locationId: string;
  /** Extra controls in the header row (e.g. owner shop picker). */
  headerExtra?: ReactNode;
  backLink: { to: string; label: string };
}

export function ScheduleWorkspace({
  locationId,
  headerExtra,
  backLink,
}: ScheduleWorkspaceProps) {
  const { user, hasRole } = useAuth();
  const [location, setLocation] = useState<Location | null>(null);
  const [users, setUsers] = useState<UserListEntry[]>([]);
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [vacations, setVacations] = useState<VacationRequest[]>([]);
  const [month, setMonth] = useState<Date>(() => startOfScheduleMonth(new Date()));
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [activeDay, setActiveDay] = useState<Date | null>(null);

  const todayCellRef = useRef<HTMLDivElement | null>(null);
  const scrollTodayAfterLayout = useRef(false);

  useLayoutEffect(() => {
    if (!scrollTodayAfterLayout.current) return;
    scrollTodayAfterLayout.current = false;
    todayCellRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [month]);

  const canEdit = hasRole("OWNER", "MANAGER");
  // D-90: whether this person sees and sets shift pay rates is the server's
  // call (OD-79), sent with the session — never restated here.
  const payVisible = user?.payVisible === true;

  const refresh = useCallback(async () => {
    if (!locationId || !user) return;
    setLoading(true);
    setError(null);
    try {
      const from = startOfScheduleMonth(month);
      const to = endOfScheduleMonth(month);
      const requests: [
        Promise<Location[]>,
        Promise<Shift[]>,
        Promise<VacationRequest[]>,
      ] = [
        api.listLocations(),
        api.listShifts({
          locationId,
          from: scheduleDayKey(from),
          to: scheduleDayKey(to),
        }),
        // G-8: approved vacations for the visible month — markers only, so a
        // failure here never blanks the schedule itself.
        api
          .listApprovedVacationsForLocation(locationId, {
            from: scheduleDayKey(from),
            to: scheduleDayKey(to),
          })
          .catch(() => [] as VacationRequest[]),
      ];
      const [locs, list, approvedVacations] = await Promise.all(requests);
      setLocation(locs.find((l) => l.id === locationId) ?? null);
      setShifts(list);
      setVacations(approvedVacations);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not load schedule");
    } finally {
      setLoading(false);
    }
  }, [locationId, month, user]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // The assignee directory does not change with the month or with a shift
  // edit, so it is fetched once per signed-in person, not on every refresh.
  // Owners and managers may list users (managers see their florists plus
  // housekeeping, G-103 / OD-72); anyone else gets no directory and no picker.
  useEffect(() => {
    if (!user || !hasRole("OWNER", "MANAGER")) {
      setUsers([]);
      return;
    }
    let cancelled = false;
    api
      .listUsers()
      .then((rows) => {
        if (!cancelled) setUsers(rows);
      })
      .catch(() => {
        if (!cancelled) setUsers([]);
      });
    return () => {
      cancelled = true;
    };
  }, [user, hasRole]);

  // Owner mode = the whole directory; manager mode = self + this shop's
  // florists + every housekeeping person (G-103 / OD-72). `GET /users` is
  // scoped server-side, so both modes read the same fetched list.
  const candidateUsers = useMemo(() => {
    if (!user) return [];
    return buildScheduleCandidateUsers({
      mode: hasRole("OWNER") ? "owner" : "manager",
      users,
      shifts,
      currentUser: user,
    });
  }, [hasRole, users, shifts, user]);

  const monthLabel = useMemo(
    () =>
      month.toLocaleDateString(undefined, {
        month: "long",
        year: "numeric",
      }),
    [month],
  );

  const shiftsByDay = useMemo(() => {
    const map = new Map<string, Shift[]>();
    for (const s of shifts) {
      const key = scheduleDayKey(new Date(s.plannedStart));
      const list = map.get(key) ?? [];
      list.push(s);
      map.set(key, list);
    }
    for (const list of map.values()) {
      list.sort(
        (a, b) =>
          new Date(a.plannedStart).getTime() -
          new Date(b.plannedStart).getTime(),
      );
    }
    return map;
  }, [shifts]);

  // G-8: expand each approved vacation's inclusive day range into per-day
  // entries, clamped to the visible month window.
  const vacationsByDay = useMemo(() => {
    const map = new Map<string, VacationRequest[]>();
    const windowStart = startOfScheduleMonth(month);
    const windowEnd = endOfScheduleMonth(month);
    for (const vacation of vacations) {
      const start = new Date(`${vacation.startDate.slice(0, 10)}T00:00:00`);
      const end = new Date(`${vacation.endDate.slice(0, 10)}T00:00:00`);
      const cursor = start < windowStart ? new Date(windowStart) : new Date(start);
      const stop = end > windowEnd ? windowEnd : end;
      for (; cursor <= stop; cursor.setDate(cursor.getDate() + 1)) {
        const key = scheduleDayKey(cursor);
        const list = map.get(key) ?? [];
        list.push(vacation);
        map.set(key, list);
      }
    }
    return map;
  }, [vacations, month]);

  const locationTitle = location?.name ?? "—";

  return (
    <section
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "var(--space-4)",
      }}
    >
      <div className="page-header">
        <div>
          <h2 className="page-title">Schedule · {locationTitle}</h2>
          <p className="page-subtitle">
            Monthly calendar of planned shifts and clock-in/out for {locationTitle}.
          </p>
          {headerExtra ? (
            <div style={{ marginTop: "var(--space-3)" }}>{headerExtra}</div>
          ) : null}
        </div>
        <div style={{ display: "flex", gap: "var(--space-2)", flexWrap: "wrap" }}>
          <Link to={backLink.to} className="button plain">
            {backLink.label}
          </Link>
          {canEdit ? (
            <button type="button" className="button" onClick={() => setBulkOpen(true)}>
              Bulk add shifts
            </button>
          ) : null}
        </div>
      </div>

      {error ? <div className="alert error">{error}</div> : null}

      <div className="card">
        <div className="schedule-toolbar">
          <div className="schedule-month-nav">
            <button
              type="button"
              className="button tinted"
              onClick={() =>
                setMonth((cur) => new Date(cur.getFullYear(), cur.getMonth() - 1, 1))
              }
            >
              ‹ Prev
            </button>
            <span className="schedule-month-label">{monthLabel}</span>
            <button
              type="button"
              className="button tinted"
              onClick={() =>
                setMonth((cur) => new Date(cur.getFullYear(), cur.getMonth() + 1, 1))
              }
            >
              Next ›
            </button>
            <button
              type="button"
              className="button plain"
              onClick={() => {
                scrollTodayAfterLayout.current = true;
                setMonth(startOfScheduleMonth(new Date()));
              }}
            >
              Today
            </button>
          </div>
          {loading ? <span className="muted">Loading…</span> : null}
        </div>

        <div style={{ marginTop: "var(--space-4)" }}>
          <MonthCalendar
            month={month}
            todayCellRef={todayCellRef}
            renderCell={(cell) => {
              const key = scheduleDayKey(cell.date);
              const dayShifts = shiftsByDay.get(key) ?? [];
              const dayVacations = vacationsByDay.get(key) ?? [];
              return (
                <>
                  <div className="month-calendar-day">
                    <span className="month-calendar-mobile-weekday">
                      {cell.date.toLocaleDateString(undefined, { weekday: "short" })}
                    </span>
                    <span className="month-calendar-day-num">{cell.date.getDate()}</span>
                    {canEdit && cell.isCurrentMonth ? (
                      <button
                        type="button"
                        className="month-calendar-add"
                        onClick={() => setActiveDay(cell.date)}
                        aria-label={`Open shifts for ${cell.date.toDateString()}`}
                      >
                        +
                      </button>
                    ) : null}
                  </div>
                  <div className="month-calendar-shifts">
                    {dayVacations.length > 0 ? (
                      <button
                        type="button"
                        className="month-calendar-vacation"
                        onClick={() => setActiveDay(cell.date)}
                        title={dayVacations
                          .map((v) => v.user.fullName)
                          .join(", ")}
                      >
                        {/* Count form only — a name would truncate in the
                            narrow cells; full names live in the tooltip and
                            the day modal. */}
                        {`${dayVacations.length} on vacation`}
                      </button>
                    ) : null}
                    {dayShifts.slice(0, 3).map((s) => (
                      <ShiftChip
                        key={s.id}
                        shift={s}
                        onClick={() => setActiveDay(cell.date)}
                      />
                    ))}
                    {dayShifts.length > 3 ? (
                      <button
                        type="button"
                        className="button plain month-calendar-more"
                        onClick={() => setActiveDay(cell.date)}
                      >
                        +{dayShifts.length - 3} more
                      </button>
                    ) : null}
                  </div>
                </>
              );
            }}
          />
        </div>
      </div>

      {activeDay && user ? (
        <DayShiftsModal
          date={activeDay}
          locationId={locationId}
          vacations={vacationsByDay.get(scheduleDayKey(activeDay)) ?? []}
          shifts={shifts.filter((s) =>
            scheduleSameDay(new Date(s.plannedStart), activeDay),
          )}
          candidateUsers={candidateUsers}
          canEdit={canEdit}
          payVisible={payVisible}
          onClose={() => setActiveDay(null)}
          onChanged={async () => {
            await refresh();
          }}
        />
      ) : null}

      {bulkOpen ? (
        <BulkAddShiftsModal
          locationId={locationId}
          defaultFrom={startOfScheduleMonth(month)}
          defaultTo={endOfScheduleMonth(month)}
          candidateUsers={candidateUsers}
          payVisible={payVisible}
          onClose={() => setBulkOpen(false)}
          onCreated={async (result) => {
            setBulkOpen(false);
            if (result.skippedOverlap > 0 || result.skippedVacation > 0) {
              const parts = [`${result.created} shift(s) created`];
              if (result.skippedOverlap > 0) {
                parts.push(`${result.skippedOverlap} skipped due to overlap`);
              }
              if (result.skippedVacation > 0) {
                parts.push(`${result.skippedVacation} skipped — approved vacation`);
              }
              setError(parts.join(" · "));
            }
            await refresh();
          }}
        />
      ) : null}
    </section>
  );
}
